<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import type { Favoritable, FavoriteButtonSetup } from '@consumer/types/serializers'
import { getContrastRatio } from '@corp/helpers/color'
import { useFavoritesStore, useGuestFavoritesStore } from '@consumer/services/favorites/store'
import { showNotice } from '@corp/services/flash'

withDefaults(defineProps<{
  favoritable: Favoritable
  themeColor?: string
  label?: boolean
  params?: FavoriteButtonSetup['params']
}>(), { label: true, })

const favorites = useFavoritesStore()
const guestFavorites = useGuestFavoritesStore()

const backgroundColor = _computed(() => {
  return __props.themeColor || __props.favoritable.giftListItem?.listable.giftlyMetadata?.logoColor || '#FFFFFF'
})

const hasLightBackground = _computed(() => getContrastRatio(backgroundColor.value, '#FFFFFF') < 2)

let isFavorited = _ref(Boolean(__props.favoritable.favoriteId) || guestFavorites.hasFavorite(__props.favoritable))

const labelText = _computed(() => isFavorited.value ? 'Added to favorites' : 'Add to favorites')

async function toggleFavorite (e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()

  if (isFavorited.value) {
    isFavorited.value = false
    favorites.removeFavorite(__props.favoritable).then(() => {
      showNotice('Successfully removed from favorites.')
    }).catch(() => {
      isFavorited.value = true
    })
  }
  else {
    isFavorited.value = true // temporal value to emulate an optimistic update.
    favorites.addFavorite(__props.favoritable, __props.params).then((response) => {
      if (response) {
        __props.favoritable.favoriteId = response.id
      }
      else {
        isFavorited.value = false
      }
    }).catch(() => {
      isFavorited.value = false
    })
  }
}
</script>

<template>
  <GButton
    v-tooltip="{content: label ? undefined : labelText, distance: 10}"
    flat
    size="large"
    class="favorite-button"
    :class="{
      'no-label': !label,
      'w-full': label,
      'lg:opacity-100 active': isFavorited,
      'light': !hasLightBackground,
    }"
    name="Toggle Favorite"
    @click="toggleFavorite"
  >
    <span
      class="flex items-center justify-center rounded-full bg-white"
      :class="label && 'bg-opacity-50'"
      :style="label ? 'height: 36px; width: 36px' : 'padding: 7px 7px 5px;'"
    >
      <FavoriteIcon
        class="transition-all duration-300 stroke-3"
        :class="{
          'text-red-intense': isFavorited,
        }"
        :style="[
          label && {marginLeft: '2px'},
          {
            fill: isFavorited ? 'currentColor' : 'transparent',
            stroke: isFavorited ? 'currentColor' : 'var(--stroke-color)',
          }
        ]"
      />
    </span>
    <span v-if="label" class="transition-all duration-300">
      {{ labelText }}
    </span>
  </GButton>
</template>

<style scoped lang="scss">
.g-button.g-button-flat.favorite-button {
  --color-focus: black;
  --color: theme("colors.grey.900");
  --stroke-color: var(--color);

  @apply gap-2;

  border: none;
  text-decoration: none;

  &:focus:not(:hover) {
    color: var(--color); // Ensure we reset the color after it's clicked.
  }

  &.no-label {
    @apply shadow-lg rounded-full;
    --stroke-color: theme("colors.grey.500");
  }

  &.light {
    --color: white;
    --color-focus: theme("colors.grey.400");
  }
}
</style>
